import { defineStore } from 'pinia';
import notificationWrapper from '@/utils/helpers/notificationWrapper';
import demandHelper from '@/utils/helpers/demand';
import { useDemandStore } from './demandStore';
import type { Project, ProjectState } from '@/types/Projects';

// Save token project in session and local storage
function setProjectSelectedStorage(token: string) {
  if (token) {
    sessionStorage.setItem('projectSelected', token);
    localStorage.setItem('projectSelected', token);
  }
}

export const useProjectStore = defineStore('project', {
  state: (): ProjectState => ({
    projects: [],
    projectSelected: null,
    isProjectsLoading: false,
    meetingsSlots: null,
    isMeetingsSlotsLoading: false,
    meetingsParams: null,
    isMeetingLoading: false,
    isCancelLoading: false,
    nbDaysBookingLimit: null,
    projectStorageKey: 'projectSelected',
  }),

  getters: {
    projectIdSelected: (state) => state.projectSelected?.token,
  },

  actions: {
    async getProjects() {
      this.isProjectsLoading = true;
      try {
        const data = await window.appConnector.getProjects();
        this.projects = data;
        let index = 0;
        // Get project token in session or local storage
        // For set project selected
        const projectSelected = sessionStorage.getItem(this.projectStorageKey)
          || localStorage.getItem(this.projectStorageKey);
        if (projectSelected) {
          const projectFindIndex = data.findIndex((project: any) => project.token === projectSelected);
          if (projectFindIndex !== -1) {
            index = projectFindIndex;
          }
        }
        setProjectSelectedStorage(data[index] && data[index].token);
        this.setProjectSelected(data[index]);
        this.isProjectsLoading = false;
      } catch (error) {
        this.isProjectsLoading = false;
        notificationWrapper('error', error.isAuthenticationError
          ? 'NOT_LOGGED'
          : 'DEFAULT');
        throw error;
      }
    },

    setProjectSelected(projectSelected: Project | null) {
      setProjectSelectedStorage(projectSelected?.token || '');
      this.projectSelected = projectSelected;
      if (projectSelected) {
        this.nbDaysBookingLimit = demandHelper.formatDay(projectSelected.params.days_booking_limit);
      }
    },

    async getMeetingsSlots(params: any) {
      this.meetingsParams = params;
      this.isMeetingsSlotsLoading = true;
      try {
        const data = await window.appConnector.getMeetingsSlots(this.projectIdSelected, { params });
        this.meetingsSlots = data.meetings;
        this.isMeetingsSlotsLoading = false;
      } catch (error) {
        this.isMeetingsSlotsLoading = false;
        notificationWrapper('error', error.isAuthenticationError
          ? 'NOT_LOGGED'
          : 'DEFAULT');
        throw error;
      }
    },

    async postMeeting(slot: any) {
      this.isMeetingLoading = true;
      try {
        await window.appConnector.postMeeting(this.projectIdSelected, slot);
        const demandStore = useDemandStore();
        await demandStore.getDemand();
        notificationWrapper('success', 'POST_MEETING');
        this.isMeetingLoading = false;
      } catch (error) {
        await this.getMeetingsSlots(this.meetingsParams);
        this.isMeetingLoading = false;
        if (error.status === 404) {
          notificationWrapper('error', 'POST_MEETING_404');
        } else {
          notificationWrapper('error', error.isAuthenticationError
            ? 'NOT_LOGGED'
            : 'DEFAULT');
          throw error;
        }
      }
    },

    async deleteMeeting(params: any) {
      this.isCancelLoading = true;
      try {
        await window.appConnector.deleteMeeting(this.projectIdSelected, params);
        const demandStore = useDemandStore();
        await demandStore.getDemand();
        this.isCancelLoading = false;
        notificationWrapper('success', 'DELETE_MEETING');
      } catch (error) {
        notificationWrapper('error', error.isAuthenticationError
          ? 'NOT_LOGGED'
          : 'DEFAULT');
        this.isCancelLoading = false;
        throw error;
      }
    },
  },
});
